import { computed, ref, watch } from 'vue';
import SalesPendingChargesAPI from 'dashboard/api/salesPendingCharges';

const messageFrom = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

const downloadBlob = (response, fallbackName) => {
  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = fallbackName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export function useSalesPendingCharge({
  transactionId,
  installmentId,
  t,
  notify,
}) {
  const charge = ref(null);
  const error = ref('');
  const loading = ref(false);
  const saving = ref(false);
  const authorizing = ref(false);
  const downloading = ref(false);
  const authorizedNow = ref(false);
  let requestSequence = 0;

  const completed = computed(() => Boolean(charge.value?.pendencia_concluida));
  const actions = computed(() => charge.value?.acoes || {});

  const load = async () => {
    requestSequence += 1;
    const sequence = requestSequence;
    charge.value = null;
    error.value = '';
    authorizedNow.value = false;
    if (!transactionId.value || !installmentId.value) return null;

    loading.value = true;
    try {
      const response = await SalesPendingChargesAPI.show(
        transactionId.value,
        installmentId.value
      );
      if (sequence !== requestSequence) return null;
      charge.value = response.data;
      return charge.value;
    } catch (requestError) {
      if (sequence !== requestSequence) return null;
      error.value = messageFrom(
        requestError,
        t('SALES.PENDING_CHARGE.ERRORS.LOAD')
      );
      return null;
    } finally {
      if (sequence === requestSequence) loading.value = false;
    }
  };

  const updatePayers = async responsibilities => {
    saving.value = true;
    try {
      const response =
        await SalesPendingChargesAPI.updatePaymentResponsibilities(
          transactionId.value,
          installmentId.value,
          responsibilities
        );
      charge.value = response.data;
      notify(t('SALES.PENDING_CHARGE.ALERTS.PAYERS_SAVED'), null, {
        variant: 'success',
      });
      return charge.value;
    } catch (requestError) {
      notify(
        messageFrom(requestError, t('SALES.PENDING_CHARGE.ERRORS.PAYERS'))
      );
      return null;
    } finally {
      saving.value = false;
    }
  };

  const authorize = async () => {
    authorizing.value = true;
    try {
      const response = await SalesPendingChargesAPI.authorize(
        transactionId.value,
        installmentId.value
      );
      authorizedNow.value = true;
      charge.value = response.data;
      return charge.value;
    } catch (requestError) {
      notify(
        messageFrom(requestError, t('SALES.PENDING_CHARGE.ERRORS.AUTHORIZE'))
      );
      return null;
    } finally {
      authorizing.value = false;
    }
  };

  const downloadSheet = async () => {
    downloading.value = true;
    try {
      const response = await SalesPendingChargesAPI.downloadSheet(
        installmentId.value
      );
      downloadBlob(
        response,
        `planilha-comissao-parcela-${installmentId.value}.pdf`
      );
      return true;
    } catch (requestError) {
      notify(
        messageFrom(requestError, t('SALES.PENDING_CHARGE.ERRORS.DOWNLOAD'))
      );
      return false;
    } finally {
      downloading.value = false;
    }
  };

  watch([transactionId, installmentId], load, { immediate: true });

  return {
    actions,
    authorize,
    authorizedNow,
    authorizing,
    charge,
    completed,
    downloadSheet,
    downloading,
    error,
    load,
    loading,
    saving,
    updatePayers,
  };
}
